/**
 * Full-screen cinematic celebration when all onboarding quests are completed.
 * Triggered globally via triggerQuestComplete().
 */
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sfxRankUp } from "@/lib/sfx";
import { Trophy, Star, Sparkles, Crown } from "lucide-react";

// ─── Event bus ───────────────────────────────────────────────
type Listener = () => void;
const listeners = new Set<Listener>();
export const triggerQuestComplete = () => {
  listeners.forEach((fn) => fn());
};

// ─── Confetti particles ─────────────────────────────────────
interface Confetti {
  id: number;
  x: number;
  delay: number;
  size: number;
  color: string;
  rotation: number;
  duration: number;
}

let confettiId = 0;
const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--concert-warm))",
  "hsl(var(--concert-glow))",
  "hsl(var(--concert-rose))",
  "#FFD700",
  "#FFA500",
  "#FF6B6B",
  "#7C3AED",
  "#00D4FF",
  "#22C55E",
];

const generateConfetti = (count: number): Confetti[] =>
  Array.from({ length: count }, () => ({
    id: ++confettiId,
    x: Math.random() * 100,
    delay: Math.random() * 1.5,
    size: 6 + Math.random() * 10,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotation: Math.random() * 720 - 360,
    duration: 2.5 + Math.random() * 2,
  }));

// ─── Floating star ──────────────────────────────────────────
const FloatingStar = ({ delay, x, size }: { delay: number; x: number; size: number }) => (
  <motion.div
    className="absolute text-primary/40"
    style={{ left: `${x}%`, top: "50%" }}
    initial={{ opacity: 0, scale: 0, y: 0 }}
    animate={{
      opacity: [0, 0.8, 0],
      scale: [0, 1, 0.5],
      y: [-100, -250],
      rotate: [0, 180],
    }}
    transition={{ duration: 3, delay, ease: "easeOut" }}
  >
    <Star className="fill-current" style={{ width: size, height: size }} />
  </motion.div>
);

// ─── Component ──────────────────────────────────────────────
const QuestCompleteCelebration = () => {
  const [show, setShow] = useState(false);
  const [confetti, setConfetti] = useState<Confetti[]>([]);

  const handleTrigger = useCallback(() => {
    setShow(true);
    setConfetti(generateConfetti(60));
    sfxRankUp();

    setTimeout(() => {
      setShow(false);
      setConfetti([]);
    }, 6000);
  }, []);

  useEffect(() => {
    listeners.add(handleTrigger);
    return () => { listeners.delete(handleTrigger); };
  }, [handleTrigger]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          onClick={() => { setShow(false); setConfetti([]); }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-background/95 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Confetti rain */}
          {confetti.map((c) => (
            <motion.div
              key={c.id}
              className="absolute rounded-sm"
              style={{
                left: `${c.x}%`,
                top: -20,
                width: c.size,
                height: c.size * 0.6,
                backgroundColor: c.color,
              }}
              initial={{ y: 0, opacity: 1, rotate: 0 }}
              animate={{
                y: "110vh",
                opacity: [1, 1, 0.6, 0],
                rotate: c.rotation,
              }}
              transition={{
                duration: c.duration,
                delay: c.delay,
                ease: [0.2, 0, 0.8, 1],
              }}
            />
          ))}

          {/* Floating stars */}
          {[15, 30, 50, 70, 85].map((x, i) => (
            <FloatingStar key={i} delay={0.3 + i * 0.2} x={x} size={20 + i * 5} />
          ))}

          {/* Expanding ring pulses */}
          {[0, 0.2, 0.4].map((delay, i) => (
            <motion.div
              key={i}
              className="absolute left-1/2 top-1/2 rounded-full border border-primary/30"
              initial={{ width: 0, height: 0, x: "-50%", y: "-50%", opacity: 0.6 }}
              animate={{
                width: 400 + i * 200,
                height: 400 + i * 200,
                x: "-50%",
                y: "-50%",
                opacity: 0,
              }}
              transition={{ duration: 1.5, delay, ease: "easeOut" }}
            />
          ))}

          {/* Center content */}
          <motion.div
            className="relative z-10 flex flex-col items-center gap-5 text-center px-8 max-w-md"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.15 }}
          >
            {/* Trophy with glow */}
            <motion.div
              className="relative"
              animate={{
                scale: [1, 1.1, 1],
                y: [0, -8, 0],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="relative flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40">
                <motion.div
                  className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/30 via-concert-warm/20 to-concert-glow/30 blur-2xl"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <Trophy className="w-20 h-20 sm:w-24 sm:h-24 text-primary drop-shadow-lg" strokeWidth={1.5} />
                <motion.div
                  className="absolute -top-2 -right-2"
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Crown className="w-8 h-8 text-concert-warm" />
                </motion.div>
                <motion.div
                  className="absolute -bottom-1 -left-1"
                  animate={{ scale: [0.8, 1.2, 0.8] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Sparkles className="w-6 h-6 text-concert-glow" />
                </motion.div>
              </div>
            </motion.div>

            {/* Badge label */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35, type: "spring", stiffness: 300, damping: 20 }}
            >
              <p className="text-xs sm:text-sm font-display font-bold uppercase tracking-[0.35em] text-primary">
                Achievement Unlocked
              </p>
            </motion.div>

            {/* Title */}
            <motion.h1
              className="text-3xl sm:text-5xl font-display font-black"
              initial={{ y: 40, opacity: 0, scale: 0.8 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 200, damping: 15 }}
            >
              <span className="bg-gradient-to-r from-primary via-concert-warm to-concert-glow bg-clip-text text-transparent">
                Quest Master
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              className="text-sm sm:text-base text-muted-foreground max-w-xs leading-relaxed"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 0.9 }}
              transition={{ delay: 0.7 }}
            >
              You've completed every onboarding quest. Welcome to the Republic — the stage is yours! 🎤
            </motion.p>

            {/* Points earned badge */}
            <motion.div
              className="flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-5 py-2.5"
              initial={{ y: 20, opacity: 0, scale: 0.9 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ delay: 0.9, type: "spring" }}
            >
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-display font-bold text-primary">
                All Bonus Points Earned
              </span>
            </motion.div>

            {/* Dismiss hint */}
            <motion.p
              className="text-xs text-muted-foreground/50 mt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
            >
              tap anywhere to dismiss
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuestCompleteCelebration;
